<script>
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import { ReplaceMeSelectionType } from '@/shared/constants/replaceMe/ReplaceMeSelectionType'
import ReplaceMeForm from '@/components/replaceMe/replace-me-form.vue'
import ReplaceMeMapper from '@/services/mappers/ReplaceMeMapper'
import getSelectedDayText from '@/helpers/get-replace-me-selected-day-text'

export default {
  name: 'ReplaceMeContainer',
  components: {
    ReplaceMeForm,
  },
  mixins: [baseMixin],
  data() {
    return {
      replaceMeStatusCheck: null,
      showReplaceMeDialog: false,
      payload: {},
      statusCheckSuccess: false,
    }
  },
  computed: {
    ...mapGetters({
      replaceMeEnabled:
        'auth/hasReplaceMePermissionForAtleastOneClientLocation',
      isLoadingReplaceMeStatusCheck: 'bookings/isLoadingReplaceMeStatusCheck',
      getSelectedClients: 'client/getSelectedClients',
      getFirstClientLocationWithReplaceMePermissions:
        'auth/getFirstClientLocationWithReplaceMePermissions',
      getUserStandardBookingDetails: 'auth/getUserStandardBookingDetails',
    }),
    isReplaceMeEnabled() {
      return this.replaceMeEnabled(this.getSelectedClients[0]) // TODO: Rework to use multiple clients
    },
    isTodayActive() {
      return this.replaceMeStatusCheck?.replaceMeTodayActive
    },
    isTodayAlreadySet() {
      return !!this.replaceMeStatusCheck?.replaceMeBookingSet?.today
    },
    compTodayText() {
      return this.isTodayAlreadySet
        ? this.$t('replaceMe.scheduledReplacementBtnText', {
            selectedDay: this.$t('replaceMe.todayLabel'),
          })
        : this.$t('replaceMe.todayLabel')
    },
    compTodayIcon() {
      if (this.isTodayActive) return 'mdi-calendar-today'
      if (this.isTodayAlreadySet) return 'mdi-check'
      return 'mdi-calendar-remove'
    },
    compTomorrowIcon() {
      if (this.isTomorrowActive) return 'mdi-calendar'
      if (this.isTomorrowAlreadySet) return 'mdi-check'
      return 'mdi-calendar-remove'
    },
    isTomorrowActive() {
      return this.replaceMeStatusCheck?.replaceMeTomorrowActive
    },
    isTomorrowAlreadySet() {
      return !!this.replaceMeStatusCheck?.replaceMeBookingSet?.tomorrow
    },
    isNextDayMonday() {
      return this.replaceMeStatusCheck?.nextDayMonday
    },
    compTomorrowText() {
      const nextMondayOrTomorrow = getSelectedDayText(
        ReplaceMeSelectionType.TOMORROW,
        this.isNextDayMonday
      )
      if (this.isTomorrowActive) return nextMondayOrTomorrow
      return this.$t('replaceMe.scheduledReplacementBtnText', {
        selectedDay: nextMondayOrTomorrow,
      })
    },
    compTodayTooltipText() {
      if (!this.isTodayAlreadySet)
        return this.$t('replaceMe.todayUnavailableTooltip')
      return this.generateReplaceMeBookingTooltipTextFromBookingStatus(
        this.replaceMeStatusCheck?.replaceMeBookingSet?.today,
        ReplaceMeSelectionType.TODAY
      )
    },
    compTomorrowTooltipText() {
      if (!this.isTomorrowAlreadySet) return ''
      return this.generateReplaceMeBookingTooltipTextFromBookingStatus(
        this.replaceMeStatusCheck?.replaceMeBookingSet?.tomorrow,
        ReplaceMeSelectionType.TOMORROW
      )
    },
  },
  async created() {
    this.initReplaceMeStatusCheck()
  },
  methods: {
    ...mapActions({
      checkReplaceMeStatus: 'bookings/checkReplaceMeStatus',
      loadClientGrades: 'client/loadClientGrades',
    }),
    async initReplaceMeStatusCheck(forceRefresh) {
      if (!this.isReplaceMeEnabled) return
      const response = await this.checkReplaceMeStatus(forceRefresh)
      if (response.isSuccess) this.replaceMeStatusCheck = response.data
      this.statusCheckSuccess = response.isSuccess
    },
    todaySelected() {
      this.displayDialog(ReplaceMeSelectionType.TODAY)
    },
    tomorrowSelected() {
      this.displayDialog(ReplaceMeSelectionType.TOMORROW)
    },
    generateReplaceMeBookingTooltipTextFromBookingStatus(booking, selectedDay) {
      const dayText = getSelectedDayText(selectedDay, this.isNextDayMonday)

      switch (booking.status) {
        case 99:
          return this.$t(
            'replaceMe.replacementScheduledPendingApprovalTooltipText',
            { selectedDay: dayText }
          )
        case 15:
          return this.$t(
            'replaceMe.replacementScheduledBookingCreatedTooltipText',
            { selectedDay: dayText, bookingId: booking.resultantBookingId }
          )
        case 20:
          return this.$t(
            'replaceMe.replacementScheduledReplacementFoundTooltipText',
            { selectedDay: dayText, bookingId: booking.resultantBookingId }
          ) // Currently not in use, as RMB is not updated when booking is filled
        default:
          return ''
      }
    },
    async displayDialog(selectedDay) {
      // Preload required data for form
      this.loadClientGrades(this.getSelectedClients[0]) // TODO: Replace with selected client id data prop

      this.payload = ReplaceMeMapper(
        this.getUserStandardBookingDetails,
        selectedDay,
        this.isNextDayMonday,
        this.getFirstClientLocationWithReplaceMePermissions(
          this.getSelectedClients[0] // TODO: Replace with selected client id data prop
        )
      )

      this.showReplaceMeDialog = true
    },
    closeModal(forceRefresh) {
      this.payload = {}
      if (forceRefresh) {
        this.initReplaceMeStatusCheck(forceRefresh)
        this.$emit('refreshData') // refresh booking summary data
      }
      this.showReplaceMeDialog = false
    },
  },
}
</script>

<template>
  <article v-if="isReplaceMeEnabled && statusCheckSuccess">
    <v-alert
      text
      color="primary"
      class="rounded-xl px-5 py-2"
      max-width="fit-content"
    >
      <v-card
        v-if="!isLoadingReplaceMeStatusCheck"
        class="transparent blue--text pa-0 ma-0"
        elevation="0"
      >
        <header class="align-center">
          <v-card-text
            :class="[
              !isMobileViewPort ? 'subtitle-1' : '',
              'blue--text align-center mt-2 pt-0',
            ]"
          >
            {{ $t('replaceMe.bannerTitle') }},
            {{ $t('replaceMe.bannerSubtitle') }}
          </v-card-text>
        </header>
        <v-card-text
          :class="[
            !isMobileViewPort
              ? 'd-flex flex-wrap align-center'
              : 'd-flex flex-wrap flex-column',
            'pt-0 mt-0',
          ]"
        >
          <v-tooltip :disabled="isTodayActive" top>
            <template v-slot:activator="{ on, attrs }">
              <div
                id="replace-me-banner__today-btn-container"
                :class="isTodayActive ? '' : 'cursor-not-allowed'"
                v-bind="attrs"
                v-on="on"
              >
                <v-btn
                  id="replace-me-banner__today-btn"
                  large
                  outlined
                  color="blue"
                  :class="(isMobileViewPort ? 'mb-4' : '', 'custom-elevation')"
                  block
                  :disabled="!isTodayActive"
                  @click="todaySelected"
                >
                  <v-icon left>
                    {{ compTodayIcon }}
                  </v-icon>
                  <p class="mb-0"> {{ compTodayText }} </p>
                </v-btn>
              </div>
            </template>
            <span> {{ compTodayTooltipText }} </span>
          </v-tooltip>

          <p v-if="!isMobileViewPort" class="mb-0 mx-4 subtitle-2 blue--text">
            {{ $t('replaceMe.bannerBtnSeparatorText') }}
          </p>
          <v-tooltip :disabled="isTomorrowActive" top>
            <template v-slot:activator="{ on, attrs }">
              <div
                v-if="!isMobileViewPort"
                id="replace-me-banner__tomorrow-btn-container"
                :class="isTomorrowActive ? '' : 'cursor-not-allowed'"
                v-bind="attrs"
                v-on="on"
              >
                <v-btn
                  id="replace-me-banner__tomorrow-btn"
                  color="blue"
                  large
                  class="d-flex flex-rows white--text custom-elevation"
                  block
                  :disabled="!isTomorrowActive"
                  @click="tomorrowSelected"
                >
                  <v-icon left>
                    {{ compTomorrowIcon }}
                  </v-icon>
                  <p class="mb-0"> {{ compTomorrowText }} </p>
                </v-btn>
              </div>
            </template>
            <span> {{ compTomorrowTooltipText }} </span>
          </v-tooltip>
        </v-card-text>
      </v-card>
    </v-alert>
    <v-dialog v-model="showReplaceMeDialog" persistent max-width="600">
      <ReplaceMeForm :payload="payload" @closeModal="closeModal" />
    </v-dialog>
  </article>
</template>
